/// <reference types="w3c-web-serial" />

/**
 * Serial Flash Service — Orchestrates ESP device flashing via Web Serial + esptool-js.
 *
 * Manages the full flash lifecycle:
 * IDLE → CONNECTING → DETECTING → ERASING → WRITING → VERIFYING → DONE | ERROR
 */

import { ESPLoader, Transport } from 'esptool-js';
import type { IEspLoaderTerminal } from 'esptool-js';
import { FlashState, FlashProgressCallback } from './flash.types';
import type { FlashOptions as InternalFlashOptions } from './flash.types';

/** Default flash configuration for Mercury ESP8266 */
const DEFAULTS = {
  BAUD_RATE: 115200,
  ROM_BAUD_RATE: 115200,
  FLASH_OFFSET: 0x00000,
  FLASH_SIZE: '2MB',
  FLASH_MODE: 'dio',
  FLASH_FREQ: '40m',
};

export class SerialFlashService {

  /**
   * Check if Web Serial API is available in the current browser.
   */
  static isSupported(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      'serial' in navigator &&
      window.isSecureContext
    );
  }

  /**
   * Get a human-readable reason for lack of support.
   */
  static getUnsupportedReason(): string {
    if (typeof navigator === 'undefined') {
      return 'Entorno no compatible con Web Serial.';
    }
    if (!window.isSecureContext) {
      return 'Web Serial requiere HTTPS o localhost. La página actual no es un contexto seguro.';
    }
    if (!('serial' in navigator)) {
      return 'Tu navegador no soporta Web Serial. Usa Google Chrome o Microsoft Edge (versión 89+).';
    }
    return 'Web Serial no disponible por un motivo desconocido.';
  }

  /**
   * Flash firmware to a connected ESP device.
   *
   * @param binaryData - Firmware binary as Uint8Array
   * @param onProgress - Callback for progress updates
   * @param options - Optional flash parameters
   * @returns Promise<void> resolves on success, rejects on error
   */
  static async flash(
    binaryData: Uint8Array,
    onProgress: FlashProgressCallback,
    options?: InternalFlashOptions
  ): Promise<void> {
    const baudRate = options?.baudRate ?? DEFAULTS.BAUD_RATE;
    const flashOffset = options?.flashOffset ?? DEFAULTS.FLASH_OFFSET;

    let port: SerialPort | null = null;
    let transport: Transport | null = null;
    let esploader: ESPLoader | null = null;

    // Log buffer for the terminal
    const logs: string[] = [];

    const reportProgress = (state: FlashState, progress: number, message: string, extras?: { chipInfo?: string; error?: string }) => {
      onProgress({
        state,
        progress,
        message,
        chipInfo: extras?.chipInfo,
        error: extras?.error,
      });
    };

    try {
      // ── Step 1: Request serial port ──
      reportProgress(FlashState.CONNECTING, 0, 'Solicitando puerto serie...');

      try {
        port = await navigator.serial.requestPort();
      } catch (err: any) {
        if (err.name === 'NotFoundError' || err.message?.includes('No port selected')) {
          throw new FlashError(
            'No se seleccionó ningún puerto serie. Conecta el dispositivo por USB y vuelve a intentarlo.',
            FlashState.CONNECTING
          );
        }
        throw new FlashError(
          `Error al solicitar puerto serie: ${err.message || 'Permiso denegado'}`,
          FlashState.CONNECTING
        );
      }

      reportProgress(FlashState.CONNECTING, 10, 'Puerto serie seleccionado. Conectando...');

      // ── Step 2: Create transport and loader ──
      transport = new Transport(port);

      const terminal: IEspLoaderTerminal = {
        clean: () => { logs.length = 0; },
        write: (data: string) => { logs.push(data); },
        writeLine: (data: string) => { logs.push(data + '\n'); },
      };

      esploader = new ESPLoader({
        transport,
        baudrate: baudRate,
        romBaudrate: DEFAULTS.ROM_BAUD_RATE,
        terminal,
      });

      reportProgress(FlashState.CONNECTING, 20, 'Conectando al bootloader del dispositivo...');

      // ── Step 3: Connect and detect chip ──
      reportProgress(FlashState.DETECTING, 25, 'Detectando chip...');

      let chipName = '';
      try {
        chipName = await esploader.main();
        reportProgress(FlashState.DETECTING, 35, `Chip detectado: ${chipName}`, { chipInfo: chipName });
      } catch (err: any) {
        throw new FlashError(
          'No se pudo conectar al dispositivo. Asegúrate de que:\n' +
          '• El dispositivo está conectado por USB\n' +
          '• El dispositivo está en modo bootloader (mantén pulsado BOOT/FLASH y presiona RESET)\n' +
          '• El driver USB-UART está instalado',
          FlashState.DETECTING
        );
      }

      // ── Step 4: Upload stub flasher ──
      // ESP8266 firmwares at 0x00000 often use IRAM segments that overlap with the esptool stub loader.
      // To prevent the "overlapping address range" error, we skip the stub for ESP8266.
      if (!chipName.includes('8266')) {
        reportProgress(FlashState.ERASING, 40, 'Cargando flasher stub...');
        await esploader.runStub();
      } else {
        reportProgress(FlashState.ERASING, 40, 'Configurando modo de flasheo en ROM (ESP8266)...');
      }

      // ── Step 5: Write flash ──
      reportProgress(FlashState.WRITING, 45, 'Preparando escritura de firmware...');

      // Convert Uint8Array to binary string for esptool-js
      const binaryString = uint8ArrayToBinaryString(binaryData);

      const fileArray = [{ data: binaryString, address: flashOffset }];

      await esploader.writeFlash({
        fileArray,
        flashSize: DEFAULTS.FLASH_SIZE,
        flashMode: DEFAULTS.FLASH_MODE,
        flashFreq: DEFAULTS.FLASH_FREQ,
        eraseAll: false,
        compress: true,
        reportProgress: (fileIndex: number, written: number, total: number) => {
          const writeProgress = Math.round((written / total) * 100);
          const overallProgress = 45 + Math.round(writeProgress * 0.45); // 45% to 90%
          reportProgress(
            FlashState.WRITING,
            overallProgress,
            `Escribiendo firmware: ${writeProgress}% (${formatBytes(written)} / ${formatBytes(total)})`
          );
        },
      });

      // ── Step 6: Verify (MD5) ──
      reportProgress(FlashState.VERIFYING, 92, 'Verificando escritura...');
      // esptool-js verifies internally during writeFlash, so we just report completion

      // ── Step 7: Reset device ──
      reportProgress(FlashState.VERIFYING, 95, 'Reiniciando dispositivo...');
      await esploader.after('hard_reset');

      // ── Step 8: Done ──
      reportProgress(FlashState.DONE, 100, '¡Firmware programado correctamente!');

    } catch (err: any) {
      if (err instanceof FlashError) {
        reportProgress(FlashState.ERROR, 0, err.message, { error: err.message });
      } else {
        const message = normalizeError(err);
        reportProgress(FlashState.ERROR, 0, message, { error: message });
      }
      throw err;
    } finally {
      // Cleanup: disconnect transport
      if (transport) {
        try {
          await transport.disconnect();
        } catch {
          // Ignore disconnect errors
        }
      }
    }
  }
}

/**
 * Custom error class for flash-specific errors with state context.
 */
class FlashError extends Error {
  state: FlashState;
  constructor(message: string, state: FlashState) {
    super(message);
    this.name = 'FlashError';
    this.state = state;
  }
}

/**
 * Convert Uint8Array to binary string (required by esptool-js).
 */
function uint8ArrayToBinaryString(data: Uint8Array): string {
  let result = '';
  const chunkSize = 8192;
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.subarray(i, i + chunkSize);
    result += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return result;
}

/**
 * Normalize common serial/flash errors into Spanish user-friendly messages.
 */
function normalizeError(err: any): string {
  const msg = err?.message || String(err);

  if (msg.includes('Failed to open serial port') || msg.includes('NetworkError')) {
    return 'No se pudo abrir el puerto serie. ¿Está el puerto en uso por otro programa (Arduino IDE, monitor serie)?';
  }
  if (msg.includes('The port is already open')) {
    return 'El puerto serie ya está en uso. Cierra otros programas que lo estén usando.';
  }
  if (msg.includes('break condition') || msg.includes('framing error')) {
    return 'Error de comunicación serie. Verifica la conexión USB y que el dispositivo esté en modo bootloader.';
  }
  if (msg.includes('Timed out')) {
    return 'Tiempo de espera agotado al comunicar con el dispositivo. ¿Está en modo bootloader?';
  }
  if (msg.includes('Disconnected') || msg.includes('device has been lost')) {
    return 'El dispositivo se desconectó durante la programación. No desconectes el cable USB durante el proceso.';
  }

  return `Error inesperado durante la programación: ${msg}`;
}

/**
 * Format bytes to human-readable string.
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
